import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    Chip
} from '@mui/material';
import {
    LocalHospital,
    MoreVert as MoreVertIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useRTL } from '../../../hooks/useRTL';
import { useAuth } from '../../../context/AuthContext';
import { COLORS } from '../../../utils/constants';
import { formatDate } from '../../../utils/formatters';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { appointmentAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const RecentAppointments = ({ analytics, analyticsLoading, error, onRefresh }) => {
    const { t } = useTranslation();
    const { isRTL, containerProps } = useRTL();
    const { canEdit, canDelete } = useAuth();

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);

    const handleMenuOpen = (event, appointment) => {
        setAnchorEl(event.currentTarget);
        setSelectedAppointment(appointment);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleView = () => {
        if (selectedAppointment) {
            setViewDialogOpen(true);
        }
        handleMenuClose();
    };

    const handleEdit = () => {
        // Handle edit logic here
        handleMenuClose();
    };

    const handleDelete = () => {
        if (selectedAppointment) {
            setDeleteDialogOpen(true);
        }
        handleMenuClose();
    };

    const confirmDelete = async () => {
        try {
            await appointmentAPI.deleteAppointment(selectedAppointment._id);
            toast.success(t('appointments.deleteSuccess'));
            onRefresh();
        } catch (error) {
            console.error('Delete appointment error:', error);
            let errorMessage = t('appointments.deleteError');

            if (error.response?.status === 400) {
                errorMessage = error.response.data.message || 'Cannot delete this appointment. Only scheduled appointments can be deleted.';
            } else if (error.response?.status === 404) {
                errorMessage = 'Appointment not found.';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            toast.error(errorMessage);
        } finally {
            setDeleteDialogOpen(false);
            setSelectedAppointment(null);
        }
    };

    if (analyticsLoading) {
        return (
            <Paper sx={{ p: 3, ...containerProps, mb: 5 }}>
                <Typography variant="h6" gutterBottom>
                    {t('dashboard.recentAppointments')}
                </Typography>
                {error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
                        <CircularProgress />
                    </Box>
                )}
            </Paper>
        );
    }

    // --- FIX STARTS HERE ---
    // Create a lookup map for scan names from the mostRequestedScans array
    const scanNameMap = {};
    if (analytics && Array.isArray(analytics.mostRequestedScans)) {
        analytics.mostRequestedScans.forEach(scan => {
            scanNameMap[scan.scanId] = scan.name;
        });
    }
    // --- FIX ENDS HERE ---

    return (
        <>
            <Paper sx={{ p: 3, ...containerProps, mb: 5 }}>
                <Typography variant="h6" gutterBottom>
                    {t('dashboard.recentAppointments')}
                </Typography>
                <List>
                    {analytics.recentAppointments?.map((appointment, index) => {
                        const patientName = appointment.patientId?.name || 'N/A';
                        const doctorName = appointment.referredBy?.name || 'N/A';
                        const doctorSpecialization = appointment.referredBy?.specialization
                            ? ` (${appointment.referredBy.specialization.name})`
                            : '';

                        // --- FIX STARTS HERE ---
                        // Scans: use the lookup map to get scan names
                        let scanNames = 'N/A';
                        if (Array.isArray(appointment.scans) && appointment.scans.length > 0) {
                            scanNames = appointment.scans
                                .map(s => scanNameMap[s.scan] || '') // Use the map to find the name by ID
                                .filter(Boolean)
                                .join(', ') || 'N/A';
                        }
                        // --- FIX ENDS HERE ---

                        // Handle date
                        const appointmentDate = appointment.appointmentDate
                            ? formatDate(appointment.appointmentDate)
                            : '';

                        return (
                            <React.Fragment key={appointment._id}>
                                <ListItem>
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: COLORS[index % COLORS.length] }}>
                                            <LocalHospital />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={<Typography>{patientName}</Typography>}
                                        secondary={
                                            <React.Fragment>
                                                <Typography variant="body2" color="textSecondary" component="span" display="block">
                                                    {t('appointments.doctor')}: {doctorName}{doctorSpecialization}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" component="span" display="block">
                                                    {t('navigation.scans')}: {scanNames}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" component="span" display="block" sx={{ mt: 1 }}>
                                                    <Chip
                                                        size="small"
                                                        label={t(`appointments.status${appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}`)}
                                                        color={
                                                            appointment.status === 'completed' ? 'success' :
                                                                appointment.status === 'scheduled' ? 'primary' : 'error'
                                                        }
                                                        sx={{ mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }}
                                                    />
                                                    {appointmentDate}
                                                </Typography>
                                            </React.Fragment>
                                        }
                                    />
                                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, appointment)}>
                                        <MoreVertIcon />
                                    </IconButton>
                                </ListItem>
                                {index < analytics.recentAppointments.length - 1 && <Divider />}
                            </React.Fragment>
                        );
                    })}
                </List>
            </Paper>

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleView}>
                    <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
                    {t('common.view')}
                </MenuItem>
                {canEdit && (
                    <MenuItem onClick={handleEdit}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        {t('common.edit')}
                    </MenuItem>
                )}
                {canDelete && selectedAppointment && selectedAppointment.status === 'scheduled' && (
                    <MenuItem onClick={handleDelete}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                        {t('common.delete')}
                    </MenuItem>
                )}
            </Menu>

            {/* View Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={() => {
                    setViewDialogOpen(false);
                    setSelectedAppointment(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('appointments.viewTitle')}</DialogTitle>
                <DialogContent>
                    {selectedAppointment && (
                        <List>
                            <ListItem>
                                <ListItemText
                                    primary={t('appointments.patient')}
                                    secondary={selectedAppointment.patientId?.name || 'N/A'}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('appointments.doctor')}
                                    secondary={selectedAppointment.referredBy?.name || 'N/A'}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('appointments.radiologist')}
                                    secondary={selectedAppointment.radiologistId?.name || 'N/A'}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('appointments.branch')}
                                    secondary={selectedAppointment.branch?.name || 'N/A'}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('appointments.dateTime')}
                                    secondary={selectedAppointment.scheduledAt
                                        ? new Date(selectedAppointment.scheduledAt).toLocaleString()
                                        : 'N/A'
                                    }
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('common.status')}
                                    secondary={t(`appointments.status${selectedAppointment.status.charAt(0).toUpperCase() + selectedAppointment.status.slice(1)}`)}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemText
                                    primary={t('common.notes')}
                                    secondary={selectedAppointment.notes || 'N/A'}
                                />
                            </ListItem>
                        </List>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setSelectedAppointment(null);
                }}
                onConfirm={confirmDelete}
                title={t('appointments.confirmDeleteTitle')}
                message={t('appointments.confirmDeleteMsg')}
            />
        </>
    );
};

export default RecentAppointments;